/**
 * Detect other people's edits to the open trip without applying them.
 *
 * Every edit writes a trip event with an actor. This polls the event log past
 * the newest event we know about (every 15 s, and immediately whenever the app
 * comes back to the foreground — the moment that matters on a phone), and files
 * anything not by the current user in the trip-changes store. Applying is a
 * separate, explicit act (see `applyRemoteChanges`).
 */
import { fetchApi } from '@/api/client';
import { useMe } from '@/api/me';
import { ensurePreferences } from '@/api/preferences';
import { queryKeys } from '@/api/queryKeys';
import { useTripChangesStore } from '@/stores/tripChanges';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import type { TripItemEvent } from '@ultimate-checklist/shared';
import { useEffect } from 'react';

const POLL_MS = 15_000;

export function useRemoteTripChanges(tripId: string, enabled = true): void {
  const qc = useQueryClient();
  const me = useMe();
  const myId = me.data?.id;
  const track = useTripChangesStore((state) => state.track);
  const ingest = useTripChangesStore((state) => state.ingest);
  const stop = useTripChangesStore((state) => state.stop);
  const setChecker = useTripChangesStore((state) => state.setChecker);
  const setChecking = useTripChangesStore((state) => state.setChecking);
  const touchChecked = useTripChangesStore((state) => state.touchChecked);

  useEffect(() => {
    // Off while live collaboration streams the same log (see useTripSync).
    if (!myId || !enabled) {
      return;
    }
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // One poll at a time; a manual "check now" joins an in-flight poll rather
    // than being dropped.
    let inFlight: Promise<void> | null = null;

    // Anchor on the newest event as of now; everything after it is "new".
    const start = async () => {
      try {
        const events = await fetchApi<TripItemEvent[]>(`/api/trips/${tripId}/events`);
        if (!disposed) {
          track(tripId, events[0]?.id ?? null);
        }
      } catch {
        // No anchor yet; the next poll will establish one from a full fetch.
        if (!disposed) {
          track(tripId, null);
        }
      }
    };

    const poll = (): Promise<void> => {
      if (disposed) {
        return Promise.resolve();
      }
      if (inFlight) {
        return inFlight;
      }
      inFlight = runPoll().finally(() => {
        inFlight = null;
      });
      return inFlight;
    };

    const runPoll = async () => {
      setChecking(true);
      try {
        const { cursor } = useTripChangesStore.getState();
        const events = await fetchApi<TripItemEvent[]>(
          `/api/trips/${tripId}/events${cursor ? `?after=${cursor}` : ''}`,
        );
        if (disposed || useTripChangesStore.getState().tripId !== tripId) {
          return;
        }
        // Without a cursor this is the first full fetch: anchor only, nothing is
        // "new" yet.
        if (!cursor) {
          track(tripId, events[0]?.id ?? null);
          return;
        }
        ingest(
          events,
          events.filter((event) => event.actorId !== myId),
        );
        // The trip row itself (name, list width, live collaboration switch) is
        // not in the event log and never touches the board's rows, so it can
        // refresh quietly on every poll — that's how a trip turned live by
        // someone else reaches this tab.
        void qc.invalidateQueries({ queryKey: queryKeys.trips.detail(tripId), exact: true });
      } catch {
        // Transient; try again next tick.
      } finally {
        setChecking(false);
        touchChecked();
      }
    };
    // "Check for changes" in the menu / palette calls this.
    setChecker(() => poll());

    const schedule = () => {
      timer = setTimeout(async () => {
        await poll();
        if (!disposed) {
          schedule();
        }
      }, POLL_MS);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void poll();
      }
    };

    void start().then(() => {
      if (!disposed) {
        touchChecked();
        schedule();
      }
    });
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      disposed = true;
      if (timer) {
        clearTimeout(timer);
      }
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      stop();
    };
  }, [tripId, myId, enabled, qc, track, ingest, stop, setChecker, setChecking, touchChecked]);
}

/** Pull the fresh trip data in and clear the pending dots. */
export async function applyRemoteChanges(qc: QueryClient): Promise<void> {
  const { tripId } = useTripChangesStore.getState();
  if (!tripId) {
    return;
  }
  // Everything under the trip: items, labels, comments, events, removed items…
  await qc.invalidateQueries({ queryKey: queryKeys.trips.detail(tripId) });
  // Account preference: some people just want the board refreshed, no marks.
  const { highlightAppliedChanges } = await ensurePreferences(qc);
  const store = useTripChangesStore.getState();
  store.markApplied();
  if (!highlightAppliedChanges) {
    store.clearApplied();
  }
}
